<script setup lang="ts">
  import { ref, computed, onMounted, onUnmounted } from "vue";
  import { storeToRefs } from "pinia";
  import { useAccountsStore } from "@stores/accounts";
  import { useUsersStore } from "@stores/users";
  import { useCurrentUserStore } from "@stores/currentUser";
  import { userType } from "@utils/constants/userRoles";
  import { Input } from "@/components/ui/input";
  import { Select } from "@/components/episto-ui/select";
  import { Switch } from "@/components/ui/switch";
  import { Badge } from "@/components/ui/badge";
  import { Button } from "@/components/ui/button";
  import { ChevronRight, CornerDownRight, FolderPlus, Gift, ListChecks, Pencil, Trash2 } from "lucide-vue-next";
  import { useToastsStore } from "@stores/toastsStore";
  import moment from "moment";

  import { IndexPageLayout, IndexPageFilters } from "@/components/App/IndexPage";
  import { FilterDropdown } from "@/components/episto-ui/filter-dropdown";
  import { DataTableActions, type DataTableColumn, type DataTableAction } from "@/components/episto-ui/data-table";
  import { IndexTable, IndexTableName, IndexTableDate } from "@/components/episto-ui/index-table";
  import { FormDialog, FormField } from "@/components/episto-ui/form-dialog";
  import FreemiumAccountDialog from "@/components/Admin/FreemiumAccountDialog.vue";
  import HubspotCompanySelect from "@/components/Admin/HubspotCompanySelect.vue";
  import AccountGuidelinesSheet from "@/components/Admin/ScriptingGuidelines/AccountGuidelinesSheet.vue";
  import { type Prospect } from "@/components/Admin/ProspectsField.vue";
  import { ConfirmDialog } from "@/components/episto-ui/confirm-dialog";
  import { useConfirmDelete } from "@/utils/composables/useConfirmDelete";
  import { useAccountsFilters } from "@utils/composables/filters/useAccountsFilters";
  import type { Account, HubspotCompany, User } from "@/types";

  const PAGE_SIZE = 10;
  const NO_MASTER_ACCOUNT = "none";

  type AiChatAccess = "inherited" | "enabled" | "disabled";

  interface AccountRow extends Account {
    depth: number;
    hasChildren: boolean;
    isExpanded: boolean;
    subaccountsCount: number;
    masterOwnerName: string | null;
  }

  const accountsStore = useAccountsStore();
  const usersStore = useUsersStore();
  const toast = useToastsStore();
  const { accounts, accountsCount, currentPage, searchQuery, typeFilter, ownersFilter, sortColumn, sortDirection } =
    storeToRefs(accountsStore);
  const { user: loggedInUser } = storeToRefs(useCurrentUserStore());

  const currentUserType = computed(() => loggedInUser.value?.user_type ?? "");

  const canCreateAccount = computed(() => [userType.ADMIN, userType.SUPER_ADMIN].includes(currentUserType.value));
  const canCreateFreemium = computed(() =>
    [userType.ADMIN, userType.SUPER_ADMIN, userType.SALES].includes(currentUserType.value),
  );
  const canDeleteAccount = computed(() => [userType.ADMIN, userType.SUPER_ADMIN].includes(currentUserType.value));

  const { filterConfigs, activeFiltersCount, handleFilterChange, handleClear } = useAccountsFilters();

  const collapsedIds = ref(new Set<number>());

  const toggleExpanded = (id: number): void => {
    const next = new Set(collapsedIds.value);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    collapsedIds.value = next;
  };

  const filtersActive = computed(
    () => Boolean(searchQuery.value.trim()) || typeFilter.value.length > 0 || ownersFilter.value.length > 0,
  );

  const rows = computed<AccountRow[]>(() =>
    accounts.value.flatMap((account) => {
      const subAccounts = account.sub_accounts ?? [];
      const isExpanded = filtersActive.value || !collapsedIds.value.has(account.id);
      const masterRow: AccountRow = {
        ...account,
        depth: 0,
        hasChildren: subAccounts.length > 0,
        isExpanded,
        subaccountsCount: subAccounts.length,
        masterOwnerName: null,
      };
      if (!isExpanded) return [masterRow];
      return [
        masterRow,
        ...subAccounts.map((subAccount) => ({
          ...subAccount,
          depth: 1,
          hasChildren: false,
          isExpanded: false,
          subaccountsCount: 0,
          masterOwnerName: account.hubspot_owner_name ?? null,
        })),
      ];
    }),
  );

  const handleSort = (column: string) => {
    if (sortColumn.value === column) {
      sortDirection.value = sortDirection.value === "asc" ? "desc" : "asc";
    } else {
      sortColumn.value = column;
      sortDirection.value = "asc";
    }
    accountsStore.fetch(1);
  };

  const handlePageChange = (page: number) => accountsStore.fetch(page);

  const isDialogOpen = ref(false);
  const isEditing = ref(false);
  const editingFreemiumAccount = ref(false);
  const currentAccount = ref<Partial<Account>>({ name: "", created_at: undefined });
  const dialogMasterAccountId = ref<string>(NO_MASTER_ACCOUNT);
  const dialogAiChatAccess = ref<AiChatAccess>("enabled");
  const dialogAccountHasChildren = ref(false);
  const dialogHubspotCompany = ref<HubspotCompany | null>(null);
  const masterAccounts = ref<Account[]>([]);

  const masterAccountOptions = computed(() => [
    { value: NO_MASTER_ACCOUNT, label: "None — master account" },
    ...masterAccounts.value
      .filter((account) => account.id !== currentAccount.value.id)
      .map((account) => ({ value: String(account.id), label: account.name })),
  ]);

  const aiChatOptions = computed(() => [
    ...(dialogMasterAccountId.value !== NO_MASTER_ACCOUNT ? [{ value: "inherited", label: "Inherited from master account" }] : []),
    { value: "enabled", label: "Enabled" },
    { value: "disabled", label: "Disabled" },
  ]);

  const handleMasterAccountChange = (masterAccountId: string | number | string[] | number[] | null): void => {
    dialogMasterAccountId.value = masterAccountId ? String(masterAccountId) : NO_MASTER_ACCOUNT;
    if (dialogMasterAccountId.value === NO_MASTER_ACCOUNT && dialogAiChatAccess.value === "inherited") {
      dialogAiChatAccess.value = "enabled";
    }
  };

  const loadMasterAccounts = async (): Promise<void> => {
    masterAccounts.value = await accountsStore.fetchMasterAccounts();
  };

  const isFreemiumDialogOpen = ref(false);
  const isGuidelinesSheetOpen = ref(false);
  const guidelinesAccount = ref<AccountRow | null>(null);
  const freemiumInitialName = ref("");
  const freemiumInitialProspects = ref<Prospect[]>([]);
  const freemiumProspectsSource = ref<string | null>(null);

  onMounted(() => accountsStore.fetch(1));
  onUnmounted(() => accountsStore.resetAllFilters());

  const columns: DataTableColumn<AccountRow>[] = [
    { key: "name", label: "Account", sortable: true },
    { key: "type", label: "Type", width: 140 },
    { key: "hubspot_owner", label: "HubSpot owner", width: 180 },
    { key: "created_at", label: "Created", sortable: true, width: 120 },
  ];

  const tableActionsFor = (row: AccountRow): DataTableAction[] => {
    const actions: DataTableAction[] = [
      { key: "edit", label: "Edit", icon: Pencil },
      { key: "scripting-guidelines", label: "Scripting guidelines", icon: ListChecks },
    ];
    if (canCreateAccount.value && row.depth === 0) {
      actions.push({
        key: "add-subaccount",
        label: "Add sub-account",
        icon: FolderPlus,
        disabled: row.freemium,
        hint: row.freemium ? "Freemium accounts can't have sub-accounts" : undefined,
      });
    }
    if (canCreateFreemium.value && !row.freemium) {
      actions.push({
        key: "freemium",
        label: "Create freemium for these users",
        icon: Gift,
      });
    }
    if (canDeleteAccount.value && row.freemium) {
      actions.push({
        key: "delete",
        label: "Delete",
        icon: Trash2,
        variant: "destructive",
        separatorBefore: true,
      });
    }
    return actions;
  };

  const formatDate = (date: string | null | undefined): string => {
    if (!date) return "-";
    return moment(date).format("YYYY-MM-DD");
  };

  const openCreateDialog = async (masterAccountId: number | null = null): Promise<void> => {
    isEditing.value = false;
    editingFreemiumAccount.value = false;
    currentAccount.value = { name: "", created_at: undefined };
    dialogMasterAccountId.value = masterAccountId ? String(masterAccountId) : NO_MASTER_ACCOUNT;
    dialogAiChatAccess.value = masterAccountId ? "inherited" : "enabled";
    dialogAccountHasChildren.value = false;
    dialogHubspotCompany.value = null;
    isDialogOpen.value = true;
    await loadMasterAccounts();
  };

  const openEditDialog = async (row: AccountRow): Promise<void> => {
    isEditing.value = true;
    editingFreemiumAccount.value = !!row.freemium;
    currentAccount.value = { ...row };
    dialogMasterAccountId.value = row.master_account_id ? String(row.master_account_id) : NO_MASTER_ACCOUNT;
    dialogAiChatAccess.value =
      row.can_create_ai_chat === null ? "inherited" : row.can_create_ai_chat ? "enabled" : "disabled";
    dialogAccountHasChildren.value = row.hasChildren;
    dialogHubspotCompany.value = row.hubspot_company_id
      ? { id: row.hubspot_company_id, name: row.hubspot_company_name ?? String(row.hubspot_company_id) }
      : null;
    isDialogOpen.value = true;
    await loadMasterAccounts();
  };

  // A freemium account is always created standalone: an account picked here is
  // only a source of users to seed it with, never a parent. Sub-accounts exist
  // one level deep, so nesting could not work from a sub-account anyway, and
  // standalone means no inheritance — the prospects are the only way in, which
  // is why FreemiumAccountDialog requires at least one.
  const openFreemiumDialog = async (row: AccountRow | null = null): Promise<void> => {
    if (!row) {
      freemiumInitialName.value = "";
      freemiumInitialProspects.value = [];
      freemiumProspectsSource.value = null;
      isFreemiumDialogOpen.value = true;
      return;
    }

    freemiumInitialName.value = `${row.name} (Freemium)`;

    try {
      const users = await usersStore.clientUsersForAccount(row.id);
      freemiumInitialProspects.value = users.map(
        (user: User): Prospect => ({
          email: user.email,
          status: "existing",
          first_name: user.first_name ?? "",
          last_name: user.last_name ?? "",
          fullName: user.full_name,
          accounts: user.accounts ?? [],
        }),
      );
      freemiumProspectsSource.value = row.name;
      isFreemiumDialogOpen.value = true;
    } catch (error) {
      console.error(error);
      toast.error("Could not load this account's users");
    }
  };

  const onFreemiumCreated = async (): Promise<void> => {
    await accountsStore.fetch(currentPage.value);
  };

  const saveAccount = async (): Promise<void> => {
    try {
      const payload: Partial<Account> = {
        name: currentAccount.value.name,
        can_create_ai_chat: dialogAiChatAccess.value === "inherited" ? null : dialogAiChatAccess.value === "enabled",
        master_account_id: dialogMasterAccountId.value === NO_MASTER_ACCOUNT ? null : Number(dialogMasterAccountId.value),
        hubspot_company_id: dialogHubspotCompany.value?.id ?? null,
      };
      if (dialogHubspotCompany.value && dialogHubspotCompany.value.id !== currentAccount.value.hubspot_company_id) {
        payload.hubspot_company_name = dialogHubspotCompany.value.name;
      }
      // Editing a freemium account exposes the conversion toggle; send the flag
      // so the API can turn it into a standard (paid) account.
      if (editingFreemiumAccount.value) payload.freemium = currentAccount.value.freemium;

      if (isEditing.value && currentAccount.value.id) {
        await accountsStore.updateAccount(currentAccount.value.id, payload);
        toast.success("Account updated successfully");
      } else {
        await accountsStore.createAccount(payload);
        toast.success("Account created successfully");
      }
      await accountsStore.fetch(currentPage.value);
      isDialogOpen.value = false;
    } catch (error) {
      console.error(error);
      toast.error("An error occurred");
    }
  };

  const {
    isOpen: isDeleteAlertOpen,
    target: accountToDelete,
    requestDelete: confirmDelete,
    confirm: handleDelete,
  } = useConfirmDelete<AccountRow>(async (row) => {
    try {
      await accountsStore.deleteAccount(row.id);
      await accountsStore.fetch(currentPage.value);
      toast.success("Account deleted successfully");
    } catch (error) {
      toast.error("Failed to delete account");
    }
  });

  const handleAction = (action: string, row: AccountRow): void => {
    switch (action) {
      case "edit":
        openEditDialog(row);
        break;
      case "add-subaccount":
        openCreateDialog(row.id);
        break;
      case "scripting-guidelines":
        guidelinesAccount.value = row;
        isGuidelinesSheetOpen.value = true;
        break;
      case "freemium":
        openFreemiumDialog(row);
        break;
      case "delete":
        confirmDelete(row);
        break;
    }
  };
</script>

<template>
  <IndexPageLayout
    title="Accounts"
    :action-label="canCreateAccount ? 'New account' : undefined"
    @action="openCreateDialog()">
    <template #actions>
      <Button
        v-if="canCreateFreemium"
        variant="ghost"
        @click="openFreemiumDialog()">
        <Gift />
        New freemium account
      </Button>
    </template>
    <template #filters>
      <IndexPageFilters
        v-model:search-model-value="searchQuery"
        search-placeholder="Search accounts">
        <template #filters>
          <FilterDropdown
            :filters="filterConfigs"
            :active-filters-count="activeFiltersCount"
            @toggle="handleFilterChange"
            @clear="handleClear" />
        </template>
      </IndexPageFilters>
    </template>

    <IndexTable
      compact
      :items="rows"
      :columns="columns"
      :current-page="currentPage"
      :total="accountsCount"
      :page-size="PAGE_SIZE"
      :sort-column="sortColumn"
      :sort-direction="sortDirection"
      empty-message="No accounts found."
      @row-click="openEditDialog"
      @sort="handleSort"
      @page-change="handlePageChange">
      <template #cell-name="{ item }">
        <div
          class="flex min-w-0 items-center"
          :style="{ paddingLeft: `${item.depth * 24}px` }">
          <IndexTableName :name="item.name">
            <template #leading>
              <button
                v-if="item.hasChildren"
                type="button"
                class="flex size-5 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary hover:text-primary"
                :aria-label="item.isExpanded ? 'Collapse sub-accounts' : 'Expand sub-accounts'"
                @click.stop="toggleExpanded(item.id)">
                <ChevronRight
                  class="size-4 transition-transform"
                  :class="{ 'rotate-90': item.isExpanded }" />
              </button>
              <CornerDownRight
                v-else-if="item.depth > 0"
                class="size-3.5 shrink-0 text-foreground-subtle" />
              <span
                v-else
                class="size-5 shrink-0" />
            </template>
            <Badge
              v-if="item.hasChildren"
              variant="neutral"
              class="font-normal">
              {{ item.subaccountsCount }} sub-account{{ item.subaccountsCount > 1 ? "s" : "" }}
            </Badge>
          </IndexTableName>
        </div>
      </template>
      <template #cell-type="{ item }">
        <Badge :variant="item.freemium ? 'freemium' : 'neutral'">
          <Gift v-if="item.freemium" />
          {{ item.freemium ? "Freemium" : "Standard" }}
        </Badge>
      </template>
      <template #cell-hubspot_owner="{ item }">
        <span
          v-if="item.hubspot_owner_name"
          class="truncate">
          {{ item.hubspot_owner_name }}
        </span>
        <span
          v-else-if="!item.hubspot_company_id && item.masterOwnerName"
          class="truncate text-muted-foreground">
          {{ item.masterOwnerName }} (master)
        </span>
        <span
          v-else
          class="text-foreground-subtle">
          {{ item.hubspot_company_id ? "No owner" : "Not linked" }}
        </span>
      </template>
      <template #cell-created_at="{ item }">
        <IndexTableDate :date="item.created_at" />
      </template>
      <template #actions="{ item }">
        <div
          class="flex items-center justify-end"
          @click.stop>
          <DataTableActions
            :actions="tableActionsFor(item)"
            @action="(action) => handleAction(action, item)" />
        </div>
      </template>
    </IndexTable>

    <template #dialogs>
      <FormDialog
        v-model:open="isDialogOpen"
        :is-editing="isEditing"
        entity-name="Account"
        @save="saveAccount">
        <FormField
          id="name"
          label="Name">
          <Input
            id="name"
            v-model="currentAccount.name"
            placeholder="Account name"
            class="text-sm" />
        </FormField>

        <FormField
          id="master_account"
          label="Master account">
          <template v-if="dialogAccountHasChildren">
            <p class="text-muted-foreground text-sm">
              This account has sub-accounts, it can't be moved under another account.
            </p>
          </template>
          <template v-else>
            <Select
              :model-value="dialogMasterAccountId"
              :options="masterAccountOptions"
              placeholder="None — master account"
              @update:model-value="handleMasterAccountChange" />
            <p class="text-muted-foreground mt-1 text-xs">
              Users of the master account automatically get access to this account.
            </p>
          </template>
        </FormField>

        <FormField
          id="hubspot_company"
          label="HubSpot company">
          <HubspotCompanySelect v-model="dialogHubspotCompany" />
          <p class="text-muted-foreground mt-1 text-xs">
            <template v-if="isEditing && currentAccount.hubspot_owner_name && dialogHubspotCompany?.id === currentAccount.hubspot_company_id">
              Owner: {{ currentAccount.hubspot_owner_name }}. Synced daily from HubSpot.
            </template>
            <template v-else>The account owner is synced daily from the HubSpot company.</template>
          </p>
        </FormField>

        <div
          v-if="editingFreemiumAccount"
          class="bg-card flex items-start gap-3 rounded-lg border p-4">
          <Gift class="mt-0.5 h-5 w-5 shrink-0" />
          <div class="flex-1">
            <div class="text-sm font-semibold">Freemium account</div>
            <div class="text-muted-foreground text-sm">
              Turn off to convert this account into a standard (paid) client. The linked users keep their access; rename
              the account above to reflect the company name.
            </div>
          </div>
          <Switch
            id="freemium"
            v-model="currentAccount.freemium" />
        </div>

        <FormField
          id="can_create_ai_chat"
          label="AI Chat access">
          <Select
            v-model="dialogAiChatAccess"
            :options="aiChatOptions" />
        </FormField>

        <FormField
          v-if="isEditing && currentAccount.created_at"
          label="Created At"
          readonly>
          {{ formatDate(currentAccount.created_at) }}
        </FormField>
      </FormDialog>

      <AccountGuidelinesSheet
        v-model:open="isGuidelinesSheetOpen"
        :account="guidelinesAccount" />

      <FreemiumAccountDialog
        :open="isFreemiumDialogOpen"
        :initial-name="freemiumInitialName"
        :initial-prospects="freemiumInitialProspects"
        :prospects-source="freemiumProspectsSource"
        @update:open="isFreemiumDialogOpen = $event"
        @created="onFreemiumCreated" />

      <ConfirmDialog
        v-model:open="isDeleteAlertOpen"
        title="Are you absolutely sure?"
        confirm-label="Delete"
        @confirm="handleDelete">
        This action cannot be undone. This will permanently delete the account
        <span class="font-bold">{{ accountToDelete?.name }}</span>
        and remove all associated data from our servers.
      </ConfirmDialog>
    </template>
  </IndexPageLayout>
</template>
